import { memo, ReactElement, useCallback, useEffect, useMemo } from "react"
import styled from "styled-components"
import Heading, { HeadingProps } from "components/Typography/Heading"
import { useAppSelector } from "reducers/store"
import { useQueryState } from "context/queryState"
import { ACCOUNT_BILLINGS } from "reducers/accounts/actions-types"
import ApiAccounts from "api/ApiAccounts"
import { CellText } from "components/EvtKit/list-items/CellText"
import { formatDate } from "utils/formatDate"
import { formatPrice } from "utils/formatPrice"
import { useDebouncedCallback } from "use-debounce"
import DatePicker from "components/EvtKit/date-picker/DatePicker"
import { CellStatus } from "components/EvtKit/list-items/CellStatus"
import DownloadBreakdownPDF from "./DownloadBreakdownPDF"
import Button from "components/EvtKit/button/Button"
import { initialDebouncedFilters, initialFilters } from "../AccountDetailsModal"
import { Colors } from "themes/palette"
import clsx from "clsx"
import IconError from "components/icons/IconError"
import BodyText from "components/Typography/BodyText"
import Spinner from "components/EvtKit/spinner/Spinner"

interface ContainerProps {
  direction?: string
  justify?: string
  align?: string
  mb?: string
}

const Container = styled.div<ContainerProps>`
  width: 100%;
  height: 100%;
`

export const Title = styled.div`
  display: flex;
  align-items: center;
  padding: 32px 0px 20px 0px;
  justify-content: space-between;
  width: 100%;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px 0px 24px 0px;
`

const ListItemsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 16px;
  /* padding-right: 24px; */
`

const ListItemsContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  background: #fff;
  background-color: ${(props) => props.theme.palette.white};
  box-shadow: 0px 2px 6px 0px rgba(13, 10, 44, 0.08);
`

const ContainerFilters = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

const ContainerIcon = styled.div`
  display: flex;
  cursor: pointer;

  &.disabled {
    cursor: not-allowed;
  }
`

const BoxContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.palette.white};
  box-shadow: 0px 2px 6px 0px rgba(13, 10, 44, 0.08);
  height: 100px;
`

enum PaymentMethod {
  "creditCard" = "Crédito",
  "invoice" = "Invoice"
}

export enum BillingAttemptStatusLabel {
  "success" = "Sucesso",
  "unsuccessful" = "Falha",
  "pending" = "Pendente",
  "noCardInformation" = "Sem dados de cartão",
  "invalidCard" = "Cartão inválido",
  "refund" = "Estornado"
}

export enum BillingAttemptStatusEnum {
  SUCCESS = "success",
  PENDING = "pending",
  UNSUCCESSFUL = "unsuccessful",
  NO_CARD_INFORMATION = "noCardInformation",
  INVALID_CARD = "invalidCard",
  REFUND = "refund"
}

const billingAttemptStatusColors: Record<BillingAttemptStatusEnum, HeadingProps["color"]> = {
  [BillingAttemptStatusEnum.UNSUCCESSFUL]: "error",
  [BillingAttemptStatusEnum.SUCCESS]: "success",
  [BillingAttemptStatusEnum.PENDING]: "informative",
  [BillingAttemptStatusEnum.NO_CARD_INFORMATION]: "error",
  [BillingAttemptStatusEnum.INVALID_CARD]: "error",
  [BillingAttemptStatusEnum.REFUND]: "error"
}

interface BillingsTableProps {
  filters: App.BillingsDateFilter
  debouncedFilters: App.BillingsDateFilter
  setFilters: React.Dispatch<React.SetStateAction<App.BillingsDateFilter>>
  setDebouncedFilters: React.Dispatch<React.SetStateAction<App.BillingsDateFilter>>
  take: string
  setTake: React.Dispatch<React.SetStateAction<string>>
}

const BillingsTable = (props: BillingsTableProps): ReactElement => {
  const { filters, setFilters, debouncedFilters, setDebouncedFilters, take, setTake } = props

  const { account } = useAppSelector((state) => state.accounts)

  const { accountDetails } = useAppSelector((state) => state.accounts)

  const { data, loading } = accountDetails.billings

  const { results, totalItems } = data

  const searchFilters = {
    start: filters.start,
    end: filters.end,
    accountId: account?.id,
    take: take
  }

  const searchParams = useMemo(() => {
    return {
      accountId: account?.id,
      start: filters.start,
      end: filters.end
    }
  }, [account, filters])

  const debouncedFiltersCallback = useDebouncedCallback(() => {
    setFilters((state) => ({
      ...state,
      ...debouncedFilters
    }))
  }, 1000)

  const handleDateFilterChange = useCallback((date: [Date | null, Date | null]) => {
    setDebouncedFilters((prevState) => ({
      ...prevState,
      start: date[0] || undefined,
      end: date[1] || undefined
    }))
  }, [])

  const seeMore = useCallback(() => {
    setTake((prevState) => String(Number(prevState) + 10))
  }, [])

  const showSeeMoreButton = useMemo(() => {
    const showButton = Number(take) < totalItems

    return showButton
  }, [take, totalItems])

  const clearFilters = useCallback(() => {
    setFilters(initialFilters)
    setDebouncedFilters(initialDebouncedFilters)
  }, [])

  const enableClearFilter = useMemo(() => Object.values(filters).some((item) => !!item), [filters])

  useEffect(() => {
    debouncedFiltersCallback()
  }, [debouncedFilters, debouncedFiltersCallback])

  useQueryState(
    [ACCOUNT_BILLINGS, searchFilters],
    async () =>
      new ApiAccounts().getAccountBillings({
        ...searchParams,
        take: Number(take)
      }),
    ACCOUNT_BILLINGS
  )

  return (
    <Container direction="column" align="center">
      <Title>
        <Heading variant="h5" color="primary-dark">
          Detalhes de Cobrança
        </Heading>
        <ContainerFilters>
          <DatePicker
            dateFormat="dd/MM/yyyy"
            onChange={(dates) => handleDateFilterChange(dates as unknown as [Date | null, Date | null])}
            startDate={debouncedFilters.start}
            endDate={debouncedFilters.end}
            selectsRange
            placeholderText="Selecione (De - Até)"
            label="Filtrar por data"
            shrink
            id="createdAt"
          />
          <ContainerIcon onClick={clearFilters} className={clsx({ disabled: !enableClearFilter })}>
            <IconError color={enableClearFilter ? Colors.primary_dark : Colors.disabled} />
          </ContainerIcon>
        </ContainerFilters>
      </Title>
      <Content>
        <ListItemsContainer>
          {!!results.length &&
            results.map((item, index) => (
              <ListItemsContent key={index}>
                <CellText title="Data" content={formatDate(item.date)} />
                <CellText title="Valor" content={formatPrice(item.price, "BRL")} />
                <CellText title="Pagamento" content={PaymentMethod[item.paymentMethod]} />
                <CellStatus
                  title="Status"
                  content={BillingAttemptStatusLabel[item.status]}
                  color={billingAttemptStatusColors[item.status]}
                />
                <DownloadBreakdownPDF pdfLink={item.pdf} />
              </ListItemsContent>
            ))}
          {!results.length && (
            <BoxContainer>
              {!loading && (
                <BodyText weight="semi-bold" size="medium" color="gray-dark">
                  Nenhuma cobrança encontrada
                </BodyText>
              )}
              {loading && <Spinner className="secondary size-medium" />}
            </BoxContainer>
          )}
          {showSeeMoreButton && <Button className="secondary" label="Ver Mais" onClick={seeMore} loading={loading} />}
        </ListItemsContainer>
      </Content>
    </Container>
  )
}

export default memo(BillingsTable)
